"use client";

import { useState, type MouseEvent, type ReactNode } from "react";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNewsletterSubscription } from "@/hooks/use-newsletter-subscription";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";

/**
 * open: signed in. pending: auth not known yet. gated: signed out. A newsletter subscribe from this
 * device does not open it: subscribing now confirms the email through sign-in, and the device flag
 * outlived sign-out, showing every design to a signed-out visitor.
 */
export type GateState = "open" | "pending" | "gated";

export function gateState({ isAuthenticated, loading }: { isAuthenticated: boolean; loading: boolean }): GateState {
  if (isAuthenticated) return "open";
  return loading ? "pending" : "gated";
}

export function openLoginDialog(description: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("open-login-dialog", { detail: { description } }));
}

/** The designs gate's copy, the same on /designs and on the app page. */
export const GATE_TITLE = "See every screen, free";
export const GATE_BODY = "Get the weekly design newsletter or sign in to unlock all designs.";

type GateNewsletter = { subscribe: (email: string) => Promise<boolean>; isLoading: boolean; error: string };

/**
 * The designs gate: signing in unlocks it (subscribing from the card confirms the email by sign-in). While
 * auth is pending nothing is drawn as locked, so a signed-in reader never sees the gate flash, but
 * clicks are held until it is known. Put `onClickCapture` on the gated area; the gate card itself
 * stays usable.
 */
export function useSignInGate(description: string, medium: string) {
  const { isAuthenticated, loading } = useAuth();
  const { subscribe, isLoading, error } = useNewsletterSubscription(medium);
  const state = gateState({ isAuthenticated, loading });
  const onClickCapture = (event: MouseEvent) => {
    if (state === "open" || (event.target as Element | null)?.closest?.("[data-sign-in-gate]")) return;
    event.preventDefault();
    event.stopPropagation();
    if (state === "gated") openLoginDialog(description);
  };
  const newsletter: GateNewsletter = { subscribe: (email) => subscribe(email), isLoading, error };
  return { state, onClickCapture, openLogin: () => openLoginDialog(description), newsletter };
}

/**
 * A teaser under a fade with the unlock card on it. With `newsletter` the card offers the design
 * newsletter first and sign-in second; without it, sign-in only. The teaser stays in the server
 * HTML for search engines: the gate is a signup nudge, not DRM.
 */
export function SignInGate({
  title,
  body,
  onSignIn,
  newsletter,
  children,
  className,
  fadeClassName = "h-72",
}: {
  title: string;
  body: string;
  onSignIn: () => void;
  newsletter?: GateNewsletter;
  children: ReactNode;
  className?: string;
  fadeClassName?: string;
}) {
  const [email, setEmail] = useState("");
  const brand = "w-full bg-[var(--ast-brand-green)] text-[var(--ast-brand-night)] hover:bg-[#52ff8d]";
  return (
    <div className={cn("relative", className)}>
      {children}
      <div className={cn("absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-b from-transparent via-background/80 to-background pb-2", fadeClassName)}>
        <div data-sign-in-gate className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted/40">
            <Lock className="h-4 w-4 text-muted-foreground" />
          </div>
          <h2 className="mt-3 text-lg font-semibold text-foreground">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p>
          {newsletter ? (
            <form
              className="mt-4 space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                void newsletter.subscribe(email);
              }}
            >
              <Input
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                aria-label="Your email"
                aria-invalid={newsletter.error ? true : undefined}
                aria-describedby={newsletter.error ? "sign-in-gate-error" : undefined}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="rounded-full px-4 ring-offset-transparent focus-visible:ring-foreground focus-visible:ring-offset-0"
              />
              {newsletter.error ? (
                <p id="sign-in-gate-error" role="alert" className="text-left text-[13px] text-destructive">
                  {newsletter.error}
                </p>
              ) : null}
              <Button type="submit" size="pill" disabled={newsletter.isLoading} className={brand}>
                {newsletter.isLoading ? <Loader2 className="animate-spin" /> : null}
                Subscribe &amp; unlock
              </Button>
              <Button type="button" variant="link" size="pill" onClick={onSignIn} className="text-muted-foreground hover:text-foreground">
                or sign in
              </Button>
            </form>
          ) : (
            <Button onClick={onSignIn} size="pill" className={cn("mt-4", brand)}>
              Sign in
              <ArrowRight />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
